import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Badge,
  Divider,
  LinearProgress,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeviceFileBrowser from '../DeviceFileBrowser';
import formatBytes from '../../utils/formatBytes';

// ---------------------------------------------------------------------------

const CATEGORIES = [
  { key: 'passive', label: 'Background', directory: 'data/passive' },
  { key: 'captures', label: 'Captures', directory: 'data/captures' },
  { key: 'logs', label: 'System Logs', directory: 'data/logs' },
];

export default function FilesTab({
  passiveFiles,
  captureFiles,
  logFiles,
  onDownload,
  onDelete,
  downloading,
  onRefresh,
  storageInfo,
  refreshing,
}) {
  const [activeCategory, setActiveCategory] = useState('passive');

  const fileMap = useMemo(
    () => ({
      passive: passiveFiles || [],
      captures: captureFiles || [],
      logs: logFiles || [],
    }),
    [passiveFiles, captureFiles, logFiles]
  );

  const activeFiles = fileMap[activeCategory] || [];
  const activeCat = CATEGORIES.find((c) => c.key === activeCategory);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0,
        height: 400,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {/* Sidebar */}
      <Box
        sx={{
          width: 200,
          flexShrink: 0,
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Folders
          </Typography>
        </Box>
        <Divider />
        <List dense disablePadding>
          {CATEGORIES.map((cat) => {
            const files = fileMap[cat.key] || [];
            return (
              <ListItemButton
                key={cat.key}
                selected={activeCategory === cat.key}
                onClick={() => setActiveCategory(cat.key)}
                sx={{ py: 0.75 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Badge
                    badgeContent={files.length}
                    color="primary"
                    max={999}
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 18, height: 18 } }}
                  >
                    <FolderIcon fontSize="small" />
                  </Badge>
                </ListItemIcon>
                <ListItemText
                  primary={cat.label}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItemButton>
            );
          })}
        </List>

        {/* Storage info at bottom */}
        {storageInfo && (
          <Box sx={{ px: 1.5, py: 1.5, mt: 'auto' }}>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              Storage
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {formatBytes(storageInfo.used)} / {formatBytes(storageInfo.total)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={storageInfo.total > 0 ? (storageInfo.used / storageInfo.total) * 100 : 0}
              sx={{ height: 4, borderRadius: 2 }}
            />
          </Box>
        )}
      </Box>

      {/* File table */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 0.75,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            {activeCat?.label}
          </Typography>
          <Tooltip title={`Refresh ${activeCat?.label}`}>
            <IconButton
              size="small"
              onClick={() => onRefresh(activeCat?.directory)}
              disabled={refreshing}
            >
              <RefreshIcon
                fontSize="small"
                sx={refreshing ? { animation: 'spin 1s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } } : {}}
              />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <DeviceFileBrowser
            files={activeFiles}
            directory={activeCat?.directory || ''}
            onDownload={onDownload}
            onDelete={onDelete}
            downloading={downloading}
          />
        </Box>
      </Box>
    </Box>
  );
}

FilesTab.propTypes = {
  passiveFiles: PropTypes.array,
  captureFiles: PropTypes.array,
  logFiles: PropTypes.array,
  onDownload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  downloading: PropTypes.string,
  onRefresh: PropTypes.func.isRequired,
  storageInfo: PropTypes.object,
  refreshing: PropTypes.bool,
};
